import { useState } from "react";
import Accordion from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import { ProductAccordionWithMonetateEvents } from "../../ProductAccordionWithMonetateEvents";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { Product } from "@/types/types";
import { Attribute } from "@/types/prodcatBaseTypes";
import styles from "./ProductMeasurementsAccordion.module.scss";

type Props = {
  product: Product;
  open?: boolean;
};

const measurementMapping = {
  dimensions: "Product dimensions",
  packageddimensions: "Boxed dimensions",
  combinedaperturedimensions: "Space needed",
  minimumclearancedimensions: "Clearance required",
  maximumappliancedepth: "Maximum depth of appliance",
};

const sellingProductTypes = [
  "Washing Machines",
  "Tumble Dryers",
  "Washer Dryers",
  "Spin Dryers",
  "Cookers",
  "Cooker Hoods",
  "Hobs",
  "Ovens",
  "Microwaves",
  "Dishwashers",
  "Freezers",
  "Fridges",
  "Fridge Freezers",
  "Wine Cabinets",
  "Televisions",
];

export function ProductMeasurementsAccordionUI({ product, open }: Props) {
  const [isOpen, setIsOpen] = useState(open);
  const { variant } = useVariant(product);
  const allMeasurements: Attribute[] = [];
  const allAttributes = [
    ...(product.attributes || []),
    ...(variant.attributes || []),
  ];
  const defaultHelpText = "Height, Width, Depth";
  const badDataList = ["mm", "n/a"];

  if (
    !product.sellingProductType ||
    !sellingProductTypes.includes(product.sellingProductType)
  ) {
    return null;
  }

  for (const [key, value] of Object.entries(measurementMapping)) {
    const measurementAttribute = allAttributes.find(
      (attribute) => attribute.key === key,
    );
    if (
      measurementAttribute &&
      measurementAttribute.values[0] &&
      !badDataList.includes(measurementAttribute.values[0].toLowerCase())
    ) {
      allMeasurements.push({ ...measurementAttribute, displayName: value });
    }
  }

  if (allMeasurements.length < 1) {
    return null;
  }

  return (
    <ProductAccordionWithMonetateEvents name="Measurements" open={isOpen}>
      <Accordion
        data-testid="accordion:measurements"
        label="Measurements"
        animated={true}
        responsive
        open={open}
        onStateChange={(isOpen: boolean) => {
          setIsOpen(isOpen);
        }}
      >
        <div data-testid="product:measurements:accordion">
          <p>
            When measuring, make sure your appliance can fit in your space and
            through doorways. The size of the space where your appliance is
            going will need to be slightly larger than the machine to allow
            space for pipes.
          </p>
          <dl data-testid="product:measurements:list">
            {allMeasurements.map((measurement) => (
              <div className={styles.measurement} key={measurement.key}>
                <dt
                  className={styles.measurementTitle}
                  data-testid="product:measurements:list:label"
                >
                  <span>{measurement.displayName}</span>
                  <ProductAttributeToolTip
                    helpText={measurement.helpText || defaultHelpText}
                    toolTipId={measurement.key}
                  />
                </dt>
                <dd
                  className={styles.measurementValue}
                  data-testid="product:measurements:list:value"
                >
                  {measurement.values[0]}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Accordion>
    </ProductAccordionWithMonetateEvents>
  );
}

function ProductAttributeToolTip({
  helpText,
  toolTipId,
}: {
  helpText: string;
  toolTipId: string;
}) {
  return (
    <div
      data-testid="product:measurements:list:label:tooltip"
      className={styles.tooltipWrapper}
    >
      <Tooltip
        id={`tooltip-${toolTipId}`}
        icon
        anchor="right"
        verticalAnchor="top"
      >
        {helpText}
      </Tooltip>
    </div>
  );
}
